import { Menu } from '@headlessui/react';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import EquipmentPhotoThumbnail from 'components/common/equipment-photo-thumbnail';
import LoadingSpinner from 'components/common/loading-spinner';
import SegmentedControl from 'components/inputs/segmented-control';
import Tooltip from 'components/layout/tooltip';
import ExistingSystemComponentForm from 'components/project-page/existing-system-component-form';
import SendPhotoCollectorModal from 'components/project-page/modals/send-photo-collector-modal';
import ChooseEquipmentImageModal from 'components/structure-page/modals/choose-equipment-image-modal';
import { ExistingSystemComponentType } from 'data/types';
import { pruneOrphanedDuctedPairLinks } from 'features/configurations';
import {
  deleteExistingSystemComponent,
  updateExistingSystemComponent
} from 'features/structure';
import { useEffect, useId, useRef, useState } from 'react';
import { getEquipmentAttachments } from 'utils/attachments';
import {
  analyzeAndUpdateEquipmentComponent,
  deleteEquipmentComponentPhotos,
  existingEquipmentComponentNeedsAnalysis,
  getAssignedEquipmentAttachments,
  isEquipmentAnalysisInFlight,
  uploadEquipmentImages
} from 'utils/equipment-images';
import {
  componentHasMissingRequiredDetails,
  getExistingEquipmentComponents,
  getExistingEquipmentTypeLabel,
  isSelfContainedComponentType
} from 'utils/existing-equipment';
import { systemComponentIsValid } from 'utils/systems';
import { errorModal } from 'utils/ui';

type EquipmentDetailsCardProps = {
  component: ExistingSystemComponentType;
};

export default function EquipmentDetailsCard({
  component
}: EquipmentDetailsCardProps) {
  const dispatch = useAppDispatch();
  const { structure, structureAttachments, existingSystems } = useAppSelector(
    (state) => state.structure
  );
  const [isUploading, setIsUploading] = useState(false);
  const [deleteQueued, setDeleteQueued] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [chooseOpen, setChooseOpen] = useState(false);
  const [photoCollectorOpen, setPhotoCollectorOpen] = useState(false);
  const [awaitingPhoneUpload, setAwaitingPhoneUpload] = useState(false);
  const autoAnalyzeStartedRef = useRef(false);
  const prevAnalyzingRef = useRef(component.analyzing);
  const phoneUploadBaselineRef = useRef<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();

  useEffect(() => {
    if (autoAnalyzeStartedRef.current) {
      return;
    }
    autoAnalyzeStartedRef.current = true;
    if (existingEquipmentComponentNeedsAnalysis(component)) {
      dispatch(
        updateExistingSystemComponent({
          uniqueId: component.uniqueId,
          analyzing: true
        })
      );
      analyzeAndUpdateEquipmentComponent(component.uniqueId);
    }
  }, [component, dispatch]);

  useEffect(() => {
    const wasAnalyzing = prevAnalyzingRef.current;
    prevAnalyzingRef.current = component.analyzing;
    if (
      wasAnalyzing &&
      !component.analyzing &&
      componentHasMissingRequiredDetails(component)
    ) {
      setDetailsExpanded(true);
    }
  }, [component]);

  const isSelfContained = isSelfContainedComponentType(component.type);

  // SC is always replacing (AHA-6796) — the keep/replace control below is
  // replaced by a static chip for SC, and this heals legacy analyzer-created
  // SC components whose toggle was flipped before the lock existed.
  useEffect(() => {
    if (isSelfContained && component.replacing === false) {
      dispatch(
        updateExistingSystemComponent({
          uniqueId: component.uniqueId,
          replacing: true
        })
      );
    }
  }, [isSelfContained, component.replacing, component.uniqueId, dispatch]);

  const level = structure.find((l) => l.uniqueId === component.levelUniqueId);
  const assignedAttachments = getAssignedEquipmentAttachments(
    component,
    structureAttachments
  );
  const assignedEquipmentImageIds = new Set(
    getExistingEquipmentComponents(existingSystems).flatMap(
      (c) => c.imageAttachmentIds ?? []
    )
  );
  const unassignedEquipmentPhotos = getEquipmentAttachments(
    structureAttachments
  ).filter((attachment) => !assignedEquipmentImageIds.has(attachment.id));

  const assignImage = (attachmentId: number) => {
    const existingIds = component.imageAttachmentIds ?? [];
    if (existingIds.includes(attachmentId)) {
      return;
    }
    const shouldAnalyze = !component.modelNumber;
    dispatch(
      updateExistingSystemComponent({
        uniqueId: component.uniqueId,
        imageAttachmentIds: [...existingIds, attachmentId],
        ...(shouldAnalyze ? { analyzing: true } : {})
      })
    );
    if (shouldAnalyze) {
      analyzeAndUpdateEquipmentComponent(component.uniqueId);
    }
  };

  const handlePhoneTextSent = () => {
    phoneUploadBaselineRef.current = new Set(
      getEquipmentAttachments(structureAttachments).map(
        (attachment) => attachment.id
      )
    );
    setAwaitingPhoneUpload(true);
  };

  useEffect(() => {
    if (!awaitingPhoneUpload) {
      return;
    }
    const baseline = phoneUploadBaselineRef.current;
    const existingIds = component.imageAttachmentIds ?? [];
    const newIds = getEquipmentAttachments(structureAttachments)
      .map((attachment) => attachment.id)
      .filter((id) => !baseline.has(id) && !existingIds.includes(id));
    if (newIds.length === 0) {
      return;
    }
    const shouldAnalyze = !component.modelNumber;
    dispatch(
      updateExistingSystemComponent({
        uniqueId: component.uniqueId,
        imageAttachmentIds: [...existingIds, ...newIds],
        ...(shouldAnalyze ? { analyzing: true } : {})
      })
    );
    if (shouldAnalyze) {
      analyzeAndUpdateEquipmentComponent(component.uniqueId);
    }
  }, [awaitingPhoneUpload, structureAttachments, component, dispatch]);

  const unassignImage = (attachmentId: number) => {
    dispatch(
      updateExistingSystemComponent({
        uniqueId: component.uniqueId,
        imageAttachmentIds: (component.imageAttachmentIds ?? []).filter(
          (id) => id !== attachmentId
        )
      })
    );
  };

  const handleUpload = async (files: File[]) => {
    setIsUploading(true);
    try {
      const attachmentIds = await uploadEquipmentImages(files);
      const existingIds = component.imageAttachmentIds ?? [];
      const newIds = attachmentIds.filter((id) => !existingIds.includes(id));
      if (newIds.length > 0) {
        const shouldAnalyze = !component.modelNumber;
        dispatch(
          updateExistingSystemComponent({
            uniqueId: component.uniqueId,
            imageAttachmentIds: [...existingIds, ...newIds],
            ...(shouldAnalyze ? { analyzing: true } : {})
          })
        );
        if (shouldAnalyze) {
          analyzeAndUpdateEquipmentComponent(component.uniqueId);
        }
      }
    } catch (error) {
      console.error('Error uploading equipment images:', error);
      errorModal('Failed to upload equipment images');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }
    await deleteEquipmentComponentPhotos(component);
    dispatch(deleteExistingSystemComponent({ uniqueId: component.uniqueId }));
    dispatch(pruneOrphanedDuctedPairLinks());
    setDeleteQueued(false);
  };

  const { analyzing } = component;
  const showAnalyzing =
    analyzing || isEquipmentAnalysisInFlight(component.uniqueId);
  const classes = clsx(
    'relative w-full flex flex-col rounded-xl border-2 shadow-sm',
    showAnalyzing ? 'bg-gray-100' : 'bg-white',
    systemComponentIsValid(component) ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div className={classes}>
      <div
        className={clsx(
          'flex flex-col',
          showAnalyzing && 'pointer-events-none opacity-40'
        )}
      >
        <div className='flex min-h-[54px] flex-wrap items-center gap-x-5 gap-y-2 border-b border-gray-200 px-5 py-2'>
          <div className='font-semibold'>
            {getExistingEquipmentTypeLabel(component)}
          </div>
          {level && (
            <span className='rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-600'>
              {level.label}
            </span>
          )}
          {isSelfContained ? (
            // No keep/replace choice for packaged units (AHA-6796): an SC
            // component is always replacing.
            <span className='rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-600'>
              Replacing
            </span>
          ) : (
            <SegmentedControl
              label={`Keep or replace ${getExistingEquipmentTypeLabel(
                component
              )}`}
              options={[
                { value: 'keeping', label: 'Keeping' },
                { value: 'replacing', label: 'Replacing' }
              ]}
              value={component.replacing ? 'replacing' : 'keeping'}
              disabled={showAnalyzing}
              onChange={(value) =>
                dispatch(
                  updateExistingSystemComponent({
                    uniqueId: component.uniqueId,
                    replacing: value === 'replacing'
                  })
                )
              }
            />
          )}
          <div className='ml-auto flex items-center'>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed'
              type='button'
              aria-label='Delete Equipment'
              disabled={showAnalyzing}
              onClick={handleDeleteClick}
              onMouseLeave={() => setDeleteQueued(false)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip
                text={
                  deleteQueued ? 'Click Again to Confirm' : 'Delete Equipment'
                }
              />
            </button>
          </div>
        </div>
        <div className='flex flex-col gap-3 px-5 py-3 lg:flex-row lg:gap-8'>
          <div className='shrink-0 lg:w-[260px]'>
            <div className='flex min-h-[200px] flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-gray-300 bg-gray-50 p-3'>
              {assignedAttachments.length > 0 && (
                <div className='flex w-full flex-wrap justify-center gap-2'>
                  {assignedAttachments.map((attachment) => (
                    <EquipmentPhotoThumbnail
                      key={attachment.id}
                      src={attachment.url ?? ''}
                      alt={attachment.name}
                      size='lg'
                      onRemove={() => unassignImage(attachment.id)}
                    />
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                id={fileInputId}
                type='file'
                accept='image/*'
                onChange={(e) => {
                  const { files } = e.currentTarget;
                  if (files && files.length > 0) {
                    const captured = Array.from(files);
                    e.currentTarget.value = '';
                    handleUpload(captured);
                  } else {
                    e.currentTarget.value = '';
                  }
                }}
                className='hidden'
                multiple
              />
              {assignedAttachments.length === 0 && (
                <div className='flex flex-col items-center gap-2'>
                  <Menu as='div' className='relative'>
                    <Menu.Button
                      type='button'
                      disabled={isUploading}
                      className='inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70'
                    >
                      {isUploading ? 'Uploading...' : 'Upload Photo'}
                      <ChevronDownIcon className='h-4 w-4' />
                    </Menu.Button>
                    <Menu.Items className='absolute left-1/2 z-10 mt-1 w-44 -translate-x-1/2 rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none'>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type='button'
                            onClick={() => fileInputRef.current?.click()}
                            className={clsx(
                              'block w-full px-3 py-2 text-left text-sm text-gray-700',
                              active && 'bg-gray-50'
                            )}
                          >
                            From computer
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type='button'
                            onClick={() => setPhotoCollectorOpen(true)}
                            className={clsx(
                              'block w-full px-3 py-2 text-left text-sm text-gray-700',
                              active && 'bg-gray-50'
                            )}
                          >
                            From phone
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Menu>
                  {unassignedEquipmentPhotos.length > 0 && (
                    <>
                      <span className='text-sm text-gray-400'>or</span>
                      <button
                        type='button'
                        onClick={() => setChooseOpen(true)}
                        className='flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50'
                      >
                        <span>Choose Photo</span>
                        <span className='flex items-center gap-1'>
                          {unassignedEquipmentPhotos
                            .slice(0, 3)
                            .map((photo) => (
                              <img
                                key={photo.id}
                                src={photo.url ?? ''}
                                alt={photo.name}
                                className='h-7 w-7 rounded border border-gray-200 object-cover'
                                draggable={false}
                              />
                            ))}
                        </span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className='min-w-0 grow'>
            <ExistingSystemComponentForm
              component={component}
              locked={showAnalyzing}
              specsCollapsible
              specsExpanded={detailsExpanded}
              onToggleSpecs={() => setDetailsExpanded((value) => !value)}
            />
          </div>
        </div>
      </div>
      {showAnalyzing && (
        <div
          className='absolute inset-0 z-10 flex items-center justify-center'
          role='status'
          aria-label='Analyzing'
        >
          <LoadingSpinner />
        </div>
      )}
      <ChooseEquipmentImageModal
        openState={[chooseOpen, setChooseOpen]}
        attachments={unassignedEquipmentPhotos}
        onSelect={(attachmentId) => {
          setChooseOpen(false);
          assignImage(attachmentId);
        }}
      />
      <SendPhotoCollectorModal
        openState={[photoCollectorOpen, setPhotoCollectorOpen]}
        subject='existing_equipment'
        onSent={handlePhoneTextSent}
      />
    </div>
  );
}
